import {inject, Injectable} from '@angular/core';
import {forkJoin, map, Observable, of, switchMap} from 'rxjs';
import {ErrorFilter} from '../../models/filter/ErrorFilter';
import {LogBookService} from './log-book.service';
import {LoggableErrorEntityService} from './loggable-error-entity.service';
import {LoggableEntity} from '../../models/error/LoggableEntity';

@Injectable({
    providedIn: 'root'
})
export class ErrorManagerService {
    private readonly overviewServices: LoggableErrorEntityService<LoggableEntity>[] = [
        inject(LogBookService),
    ]

    constructor() {
    }

    public getPaginatedErrors(pageIndex: number, pageSize: number, errorFilter: ErrorFilter): Observable<LoggableEntity[]> {
        return forkJoin(
            this.overviewServices.map(service =>
                service.getAmountOfErrorItems(errorFilter).pipe(
                    map((amountOfErrorItems) => ({service, amountOfErrorItems}))
                )
            )
        ).pipe(
            switchMap((counts) => {
                let startIndex = pageIndex * pageSize;
                let remaining = pageSize;
                const observables: Observable<LoggableEntity[]>[] = [];

                counts.forEach(count => {
                    if (remaining > 0 && startIndex < count.amountOfErrorItems) {
                        const fetchCount = Math.min(remaining, count.amountOfErrorItems - startIndex);
                        observables.push(count.service.getAll(errorFilter, fetchCount, startIndex));
                        remaining -= fetchCount;
                        startIndex = fetchCount < remaining ? 0 : startIndex;
                    } else {
                        startIndex -= count.amountOfErrorItems
                    }
                });

                return observables.length <= 0 ? of([]) : forkJoin(observables).pipe(
                    map(errorLists => errorLists.flat())
                );
            })
        );
    }

    public getTotalAmountOfErrorItems(errorFilter: ErrorFilter): Observable<number> {
        return forkJoin(
            this.overviewServices.map(service => service.getAmountOfErrorItems(errorFilter))
        ).pipe(
            map(listWithAmountOfErrorItems => listWithAmountOfErrorItems.reduce((partialSum, a) => partialSum + a, 0))
        );
    }
}
